import React, { useState } from 'react';
import { action, observable, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import {
    DepositFailure,
    DepositViewType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/depositFailure/DepositFailure';
import { MessageDataType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/Iframe';
import { RealexTabState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexTab.state';
import { RealexTab } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexTab';
import {
    RealexFrame,
    RealexFramePropsType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/realexFrame/RealexFrame';
import { DepositSignupRealex } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/DepositSignupRealex';
import { DepositFinalView } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/depositFinalView/DepositFinalView';
import { YaspaUrlResponseType } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment.state';
import { YaspaDeposit } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaDeposit';
import { useCommon } from 'src/domains/common/Common';

export type DepositStepsType =
    | {
          readonly type: 'set-method';
      }
    | {
          readonly type: 'set-method-signup';
      }
    | {
          readonly type: 'iframe-view';
          readonly formActionUrl: string;
          readonly formData: Record<string, string>;
          readonly onMessage: (data: MessageDataType) => void;
      }
    | {
          readonly type: 'iframe-view-yaspa';
          readonly url: string;
          readonly yaspaView: 'iframe-view' | 'loading-view';
      }
    | {
          readonly type: 'final-view';
          transactionId?: number;
      }
    | {
          readonly type: 'failure-view';
          readonly failureType: DepositViewType;
      };

export class RealexDepositSteps {
    @observable.ref public step: DepositStepsType;

    public constructor(private readonly isSignup: boolean) {
        makeObservable(this);
        this.step = {
            type: this.isSignup ? 'set-method-signup' : 'set-method',
        };
    }

    @action public redirectToSetMethod = (): void => {
        this.step = {
            type: this.isSignup ? 'set-method-signup' : 'set-method',
        };
    };

    @action public redirectToIframeNewCard = ({ formActionUrl, formData, onMessage }: RealexFramePropsType): void => {
        this.step = {
            type: 'iframe-view',
            formActionUrl,
            formData,
            onMessage,
        };
    };

    @action public redirectToIframeYaspa = ({ link }: YaspaUrlResponseType): void => {
        this.step = {
            type: 'iframe-view-yaspa',
            url: link,
            yaspaView: 'iframe-view',
        };
    };

    @action public redirectFromYaspaIframe = (): void => {
        this.step = {
            type: 'iframe-view-yaspa',
            yaspaView: 'loading-view',
            url: '',
        };
    };

    @action public redirectToFinalView = (transactionId: number | undefined): void => {
        this.step = {
            type: 'final-view',
            transactionId,
        };
    };

    @action public redirectToFailureView = (failureType: DepositViewType): void => {
        this.step = {
            type: 'failure-view',
            failureType,
        };
    };
}

interface PropsType {
    isSignup: boolean;
}

export const RealexJourney = observer('RealexJourney', ({ isSignup }: PropsType): JSX.Element => {
    const common = useCommon();

    const [state] = useState(() => new RealexTabState(common, isSignup));

    const renderContent = (): JSX.Element => {
        switch (state.stepsState.step.type) {
            // step 1a: choose amount
            case 'set-method':
                return <RealexTab state={state} />;
            // step 1c: signup (choose amount)
            case 'set-method-signup':
                return <DepositSignupRealex state={state} />;
            // step 2a/2c: new card iframe
            case 'iframe-view':
                return (
                    <RealexFrame
                        formActionUrl={state.stepsState.step.formActionUrl}
                        formData={state.stepsState.step.formData}
                        onMessage={state.stepsState.step.onMessage}
                    />
                );

            case 'iframe-view-yaspa':
                return (
                    <YaspaDeposit
                        src={state.stepsState.step.url}
                        onMessage={state.stepsState.redirectFromYaspaIframe}
                        yaspaView={state.stepsState.step.yaspaView}
                        accountHelperMail={state.accountHelperMail}
                        isSignup={isSignup}
                        hideDepositSuccess={state.hideDepositSuccess}
                    />
                );
            // step 3a/3c
            case 'final-view':
                return (
                    <DepositFinalView
                        transactionId={state.stepsState.step.transactionId}
                        isSignup={isSignup}
                        accountHelperMail={state.accountHelperMail}
                        hideDepositSuccess={state.hideDepositSuccess}
                        paymentProvider='realex'
                    />
                );
            // step 4a/4c
            case 'failure-view':
                return (
                    <DepositFailure
                        isSignup={isSignup}
                        issueType={state.stepsState.step.failureType}
                        accountHelperMail={state.accountHelperMail}
                    />
                );
        }
    };

    return <>{renderContent()}</>;
});
